import { apiFetch } from '@/src/api/apiService';
import { API_BASE } from '@/src/api/config';

/**
 * Server-issued registration session (D-057).
 *
 * The client never invents the registration token. The backend owns it: an
 * unguessable, short-lived, single-use session that binds a signup to the last
 * valid referral code captured for it. This helper asks the server for one and
 * returns the opaque token, or `null` when the endpoint is unavailable (the
 * caller then shows a truthful transient state and the account is still
 * created — a referral problem must never block registration, D-012).
 *
 * Contract (backend/referrals/views.py `RegistrationSessionView`):
 *   POST /api/referrals/registration-session/  -> 201
 *   { registration_token, expires_at, expires_in }
 */
export async function beginRegistrationSession(): Promise<string | null> {
  let response: Awaited<ReturnType<typeof apiFetch<{ registration_token?: string }>>>;
  try {
    response = await apiFetch<{ registration_token?: string }>(
      `${API_BASE}/api/referrals/registration-session/`,
      { method: 'POST', includeCompanyContext: false, body: JSON.stringify({}) },
      false,
      false,
    );
  } catch {
    // A rejected request is the same product outcome as an unavailable endpoint:
    // no server-issued session, so the caller shows a truthful transient state and
    // the signup still proceeds (D-012). Never let it surface as an unhandled
    // rejection — a referral problem must never block registration.
    return null;
  }
  const token = response.ok ? response.data?.registration_token : undefined;
  return typeof token === 'string' && token.trim() ? token.trim() : null;
}
